/**
 * HoursTab — Weekly operating hours editor.
 *
 * Grid layout (tablet-first):
 *   Day label (closed toggle) | Open | Close | Error
 *
 * Zod enforces open < close; on client we grey-out open/close inputs
 * when the "Closed" switch is on (and clear validation errors).
 */
import { zodResolver } from '@hookform/resolvers/zod';
import { CheckCircle } from '@phosphor-icons/react';
import { useEffect, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { Button } from '../../../shared/ui/Button';
import { FieldError } from '../../../shared/ui/FieldError';
import { Input } from '../../../shared/ui/Input';
import { Switch } from '../../../shared/ui/Switch';
import { cn } from '../../../shared/utils/cn';
import {
  useBusinessSettings,
  useUpdateBusinessSettings,
} from '../../../features/settings/useBusinessSettings';
import {
  hoursSchema,
  WEEKDAYS,
  type WeeklyHours,
  type Weekday,
} from '../../../features/settings/types';
import { SettingsCard } from './SettingsCard';

export function HoursTab(): JSX.Element {
  const { t } = useTranslation('settings');
  const settingsQuery = useBusinessSettings();
  const updateMutation = useUpdateBusinessSettings();
  const [justSaved, setJustSaved] = useState(false);

  const {
    register,
    handleSubmit,
    reset,
    control,
    watch,
    formState: { errors, isDirty, isSubmitting },
  } = useForm<WeeklyHours>({
    resolver: zodResolver(hoursSchema),
    defaultValues: settingsQuery.data?.hours,
  });

  useEffect(() => {
    if (settingsQuery.data?.hours) {
      reset(settingsQuery.data.hours);
    }
  }, [settingsQuery.data?.hours, reset]);

  const onSubmit = async (values: WeeklyHours) => {
    setJustSaved(false);
    await updateMutation.mutateAsync({ hours: values });
    reset(values);
    setJustSaved(true);
    window.setTimeout(() => setJustSaved(false), 2500);
  };

  if (settingsQuery.isLoading || !settingsQuery.data) {
    return <LoadingSkeleton />;
  }

  return (
    <SettingsCard
      title={t('hours.sectionTitle')}
      hint={t('hours.sectionHint')}
      onSubmit={handleSubmit(onSubmit)}
      footer={
        <>
          {justSaved ? (
            <span
              role="status"
              className="inline-flex items-center gap-1.5 text-biz-sm text-biz-success mr-auto"
            >
              <CheckCircle weight="regular" size={18} aria-hidden="true" />
              {t('common.saved')}
            </span>
          ) : null}
          <Button
            type="button"
            intent="secondary"
            onClick={() => settingsQuery.data && reset(settingsQuery.data.hours)}
            disabled={!isDirty || isSubmitting}
          >
            {t('common.cancel')}
          </Button>
          <Button type="submit" intent="primary" loading={isSubmitting} disabled={!isDirty}>
            {isSubmitting ? t('common.saving') : t('common.save')}
          </Button>
        </>
      }
    >
      <div className="grid gap-biz-content-lg">
        {WEEKDAYS.map((day) => {
          const isClosed = watch(`${day}.closed`);
          const closeError = errors[day]?.close;
          return (
            <div
              key={day}
              className={cn(
                'grid grid-cols-1 md:grid-cols-[160px_1fr_1fr] items-center gap-biz-content-lg',
                'py-biz-content border-b border-biz-border last:border-0',
              )}
            >
              <div className="flex items-center gap-biz-content-lg">
                <Controller
                  control={control}
                  name={`${day}.closed` as const}
                  render={({ field }) => (
                    <Switch
                      id={`${day}-open`}
                      checked={!field.value}
                      onCheckedChange={(checked) => field.onChange(!checked)}
                      aria-label={
                        field.value
                          ? `${t(`hours.day.${day}` as const)} — ${t('hours.closed')}`
                          : `${t(`hours.day.${day}` as const)} — ${t('hours.open')}`
                      }
                    />
                  )}
                />
                <label
                  htmlFor={`${day}-open`}
                  className="text-biz-base font-medium cursor-pointer"
                >
                  {t(`hours.day.${day}` as const)}
                </label>
              </div>

              <div className="flex items-center gap-biz-content-lg">
                <span className="text-biz-xs text-biz-muted-foreground w-14">
                  {t('hours.openLabel')}
                </span>
                <Input
                  type="time"
                  step={300}
                  disabled={isClosed}
                  aria-label={`${t(`hours.day.${day}` as const)} ${t('hours.openLabel')}`}
                  {...register(`${day}.open` as const)}
                />
              </div>

              <div className="flex flex-col gap-biz-content">
                <div className="flex items-center gap-biz-content-lg">
                  <span className="text-biz-xs text-biz-muted-foreground w-14">
                    {t('hours.closeLabel')}
                  </span>
                  <Input
                    type="time"
                    step={300}
                    disabled={isClosed}
                    invalid={Boolean(closeError)}
                    aria-label={`${t(`hours.day.${day}` as const)} ${t('hours.closeLabel')}`}
                    {...register(`${day}.close` as const)}
                  />
                </div>
                {closeError?.message === 'invalidRange' ? (
                  <FieldError id={`${day}-close-error`}>{t('hours.invalidRange')}</FieldError>
                ) : null}
              </div>
            </div>
          );
        })}
      </div>
    </SettingsCard>
  );
}

function LoadingSkeleton(): JSX.Element {
  return (
    <div className="space-y-biz-content" aria-busy="true" aria-live="polite">
      {([...Array(7)] as Weekday[]).map((_, i) => (
        <div
          key={i}
          className="h-14 rounded-biz-card bg-biz-muted animate-pulse"
          aria-hidden="true"
        />
      ))}
    </div>
  );
}
